// Catalog integrity rules — the single source of truth for "is the
// exercise catalog correctly populated?". Born 2026-06-16 after the THIRD
// unilateral mis-flag correction (inbox ce88ca2f): a wrong `unilateral`
// flag silently under-counts volume ×2, which is a training-safety bug
// (Antenore could add load to "compensate" a phantom drop). See
// [[feedback_volume_bilateral_unilateral_model]].
//
// ONE pure function, consumed by (a) the jest gate against the committed
// snapshot (tooling/catalog/catalog.snapshot.json) in `pnpm release`, and
// (b) the on-demand CLI against live DDB. Errors block; warnings block too
// UNLESS listed in tooling/catalog/catalog-exceptions.json with a reason —
// so a newly-added single-limb exercise with no flag turns the gate red
// until a human fixes it or records why it's intentional.

import { Exercise } from "./schemas/exercise.js";
import { CATALOG_MUSCLE_SLUGS } from "./muscleCatalog.js";

// The canonical catalog muscle vocabulary. `muscleContrib` is already
// zod-validated against this enum, but `primary[]`/`secondary[]` use a
// free-text MuscleSlug regex (no enum) — so a typo there slips past the
// schema. Validating those arrays against the SAME canonical list catches
// it. Reusing CATALOG_MUSCLE_SLUGS keeps ONE source of truth (no parallel
// list to drift). Per [[reference_jl_catalog_muscle_vocabulary]]: this is
// the catalog vocab, NOT the broader bodymap MUSCLE_SLUGS.
const VOCAB = new Set<string>(CATALOG_MUSCLE_SLUGS);

const LOCALES = ["en", "it", "fr", "de", "ru"] as const;

// Substrings that mark a lift as single-limb (one side logged → should be
// unilateral=true, volume ×2). Matched against slug AND name.en. A match
// with unilateral≠true raises `laterality_missing` — the exact class of
// bug that shipped today. Genuinely-bilateral lifts whose NAME trips a
// pattern (e.g. a two-handed wood-chop, a farmer carry) are whitelisted in
// catalog-exceptions.json, never by weakening this list.
const SINGLE_LIMB_PATTERNS = [
  "split-squat",
  "bulgarian",
  "lunge",
  "step-up",
  "pistol",
  "single-leg",
  "single-arm",
  "one-arm",
  "one-leg",
  "one-legged",
  "cossack",
  "curtsy",
  "shrimp",
  "skater",
  "b-stance",
  "staggered",
  "suitcase",
  "side-plank",
  "clamshell",
  "kickback",
  "windmill",
  "copenhagen",
  "archer",
  "prone-arm",
  "rear-foot",
  "rfess",
  "pallof",
  "wood-chop",
  "woodchop",
  "cross-body",
  "crossbody",
  "carry",
  "farmer",
] as const;

export type Severity = "error" | "warning";

export interface CatalogFinding {
  slug: string;
  rule: string;
  severity: Severity;
  message: string;
}

export interface CatalogException {
  slug: string;
  rule: string;
  reason: string;
}

export interface CatalogValidationResult {
  total: number;
  errors: CatalogFinding[];
  warnings: CatalogFinding[]; // every warning, incl. acknowledged ones
  unacknowledged: CatalogFinding[]; // warnings NOT covered by an exception
  staleExceptions: CatalogException[]; // exceptions that matched no finding
  ok: boolean; // no errors AND no unacknowledged warnings
}

// #414: the equipment where one row can mean one load or two (two cables, two
// dumbbells). There, a row must say which: `unilateral` or `lateralityModifiable`.
const TWO_LOAD_EQUIPMENT = new Set(["cable", "dumbbell"]);

type Row = Record<string, unknown> & {
  slug?: unknown;
  name?: Record<string, unknown>;
  equipment?: unknown;
  unilateral?: unknown;
  lateralityModifiable?: unknown;
  isBodyweight?: unknown;
  isTimeBased?: unknown;
  bodyweightLoadPct?: unknown;
  muscleContrib?: Record<string, unknown>;
  primary?: unknown;
  secondary?: unknown;
  alternatives?: unknown;
  painStabilizers?: unknown;
};

const lower = (v: unknown): string =>
  typeof v === "string" ? v.toLowerCase() : "";

const looksSingleLimb = (slug: string, nameEn: string): boolean =>
  SINGLE_LIMB_PATTERNS.some((p) => slug.includes(p) || nameEn.includes(p));

const stringsOf = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

const listedMuscles = (row: Row): string[] => [
  ...stringsOf(row.primary),
  ...stringsOf(row.secondary),
];

const musclesOf = (row: Row): string[] => {
  const out = listedMuscles(row);
  if (row.muscleContrib && typeof row.muscleContrib === "object") {
    out.push(...Object.keys(row.muscleContrib));
  }
  if (Array.isArray(row.painStabilizers)) {
    for (const p of row.painStabilizers) {
      const m = (p as { slug?: unknown } | null)?.slug;
      if (typeof m === "string") out.push(m);
    }
  }
  return out;
};

/**
 * Validate a list of catalog rows against the structural schema + the
 * semantic/policy rules. Pure: no IO. `rows` are raw objects (from the
 * snapshot or a live DDB read); `exceptions` acknowledge known-OK
 * warnings.
 */
export function validateCatalog(
  rows: unknown[],
  exceptions: CatalogException[] = [],
): CatalogValidationResult {
  const errors: CatalogFinding[] = [];
  const warnings: CatalogFinding[] = [];

  const seenSlug = new Map<string, number>();
  const seenNameEn = new Map<string, string[]>();
  const archived = new Set<string>();

  for (const raw of rows) {
    const row = (raw ?? {}) as Row;
    const slug = typeof row.slug === "string" ? row.slug : "<no-slug>";
    const nameEn = lower(row.name?.en);

    // E1 — structural: must satisfy the canonical zod schema.
    const parsed = Exercise.safeParse(row);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const where = issue ? `${issue.path.join(".")} — ${issue.message}` : "invalid";
      errors.push({
        slug,
        rule: "schema",
        severity: "error",
        message: `schema: ${where}`,
      });
    }

    // E2 — muscle vocabulary: every slug must be in the frozen vocab.
    for (const m of musclesOf(row)) {
      if (!VOCAB.has(m)) {
        errors.push({
          slug,
          rule: "muscle_vocab",
          severity: "error",
          message: `unknown muscle slug "${m}" (not in CATALOG_MUSCLE_VOCAB)`,
        });
      }
    }

    // E4: no timer on a catalog row (#411). The catalog is everyone's, so a
    // timer there is every athlete's; a timer is the athlete's own, in their
    // partition (schemas/exerciseTimer.ts).
    for (const field of ["defaultRestSec", "defaultDurationSec"] as const) {
      if (row[field] !== undefined && row[field] !== null) {
        errors.push({
          slug,
          rule: "catalog_timer",
          severity: "error",
          message: `${field} on a catalog row: a timer belongs to the athlete (#411)`,
        });
      }
    }

    // E3 — duplicate slug.
    seenSlug.set(slug, (seenSlug.get(slug) ?? 0) + 1);
    if (row.archived === true) archived.add(slug);

    // W1 — name not translated in all 5 locales.
    const missingLocales = LOCALES.filter(
      (l) => !(typeof row.name?.[l] === "string" && (row.name[l] as string).length),
    );
    if (missingLocales.length) {
      warnings.push({
        slug,
        rule: "name_translation_incomplete",
        severity: "warning",
        message: `name missing locale(s): ${missingLocales.join(", ")}`,
      });
    }

    // W2 — laterality: single-limb name but not flagged unilateral.
    if (row.unilateral !== true && looksSingleLimb(slug, nameEn)) {
      warnings.push({
        slug,
        rule: "laterality_missing",
        severity: "warning",
        message:
          "name looks single-limb but unilateral≠true → volume counts ×1 (likely should be ×2)",
      });
    }

    // W7 (#414): a cable or dumbbell row that sets neither flag counts one
    // load by default, and nobody chose it: the two-cable fly lost half its
    // volume that way.
    if (
      typeof row.equipment === "string" &&
      TWO_LOAD_EQUIPMENT.has(row.equipment) &&
      row.unilateral == null &&
      row.lateralityModifiable == null
    ) {
      warnings.push({
        slug,
        rule: "laterality_unset",
        severity: "warning",
        message: `${row.equipment} row with neither unilateral nor lateralityModifiable set → counts ×1 by default`,
      });
    }

    // W3 — bodyweight exercise without an explicit load pct (defaults 100%).
    if (
      row.isBodyweight === true &&
      row.isTimeBased !== true &&
      (row.bodyweightLoadPct === undefined || row.bodyweightLoadPct === null)
    ) {
      warnings.push({
        slug,
        rule: "bodyweight_pct_missing",
        severity: "warning",
        message:
          "bodyweight exercise without bodyweightLoadPct → defaults to 100% (likely over/under-counts)",
      });
    }

    // W4 — no muscleContrib (volume falls back to legacy primary[0] math).
    if (!row.muscleContrib || typeof row.muscleContrib !== "object") {
      warnings.push({
        slug,
        rule: "muscle_contrib_missing",
        severity: "warning",
        message: "no muscleContrib map → volumeByMuscle uses legacy fallback",
      });
    }

    // W6: a muscle in primary/secondary with no muscleContrib entry gets no
    // volume (#414). The reverse (a small contribution not listed) is not
    // checked: 212 rows on 22/10.
    if (row.muscleContrib && typeof row.muscleContrib === "object") {
      const mapped = Object.keys(row.muscleContrib);
      const off = listedMuscles(row).filter((m) => !mapped.includes(m));
      if (off.length) {
        warnings.push({
          slug,
          rule: "muscle_contrib_mismatch",
          severity: "warning",
          message: `in primary/secondary, not in muscleContrib: ${off.join(", ")}`,
        });
      }
    }

    // collect for dup-name pass
    if (nameEn) {
      const list = seenNameEn.get(nameEn) ?? [];
      list.push(slug);
      seenNameEn.set(nameEn, list);
    }
  }

  // E3 — emit duplicate slugs.
  for (const [slug, count] of seenSlug) {
    if (count > 1) {
      errors.push({
        slug,
        rule: "duplicate_slug",
        severity: "error",
        message: `slug appears ${count}× in the catalog`,
      });
    }
  }

  // E5: an alternative names a live catalog row other than itself (#414).
  for (const raw of rows) {
    const row = (raw ?? {}) as Row;
    for (const alt of stringsOf(row.alternatives)) {
      const [rule, message] =
        alt === row.slug
          ? ["alternative_self", "lists itself as an alternative"]
          : !seenSlug.has(alt)
            ? ["alternative_unknown", `alternative "${alt}" is not in the catalog`]
            : archived.has(alt)
              ? ["alternative_archived", `alternative "${alt}" is archived`]
              : [];
      if (!rule) continue;
      errors.push({
        slug: typeof row.slug === "string" ? row.slug : "<no-slug>",
        rule,
        severity: "error",
        message: message!,
      });
    }
  }

  // W5 — duplicate name.en across different slugs.
  for (const [nameEn, slugs] of seenNameEn) {
    if (slugs.length > 1) {
      for (const slug of slugs) {
        warnings.push({
          slug,
          rule: "duplicate_name_en",
          severity: "warning",
          message: `name.en "${nameEn}" shared with: ${slugs.filter((s) => s !== slug).join(", ")}`,
        });
      }
    }
  }

  // Partition warnings by exception coverage.
  const exKey = (slug: string, rule: string) => `${slug} ${rule}`;
  const exSet = new Set(exceptions.map((e) => exKey(e.slug, e.rule)));
  // An exception with slug:"*" acknowledges EVERY warning of that rule —
  // a deliberate, tracked group-suppression for a known backlog (e.g. a
  // pending sport-science pass), never a silent blanket.
  const wildcardRules = new Set(
    exceptions.filter((e) => e.slug === "*").map((e) => e.rule),
  );
  const usedEx = new Set<string>();
  const unacknowledged: CatalogFinding[] = [];
  for (const w of warnings) {
    const k = exKey(w.slug, w.rule);
    if (exSet.has(k)) {
      usedEx.add(k);
    } else if (wildcardRules.has(w.rule)) {
      usedEx.add(exKey("*", w.rule));
    } else {
      unacknowledged.push(w);
    }
  }
  const staleExceptions = exceptions.filter(
    (e) => !usedEx.has(exKey(e.slug, e.rule)),
  );

  return {
    total: rows.length,
    errors,
    warnings,
    unacknowledged,
    staleExceptions,
    ok: errors.length === 0 && unacknowledged.length === 0,
  };
}

/** Compact human-readable report for the CLI / test failure message. */
export function formatCatalogReport(r: CatalogValidationResult): string {
  const lines: string[] = [];
  lines.push(
    `catalog: ${r.total} rows · ${r.errors.length} errors · ${r.unacknowledged.length} unacknowledged warnings · ${r.warnings.length} warnings total`,
  );
  for (const e of r.errors) lines.push(`  ERROR  ${e.slug} [${e.rule}] ${e.message}`);
  for (const w of r.unacknowledged)
    lines.push(`  WARN   ${w.slug} [${w.rule}] ${w.message}`);
  if (r.staleExceptions.length)
    lines.push(
      `  note: ${r.staleExceptions.length} stale exception(s) (no longer matched): ${r.staleExceptions
        .map((e) => `${e.slug}/${e.rule}`)
        .join(", ")}`,
    );
  return lines.join("\n");
}
